'use client';

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertCircle, CheckCircle2, AlertTriangle, XCircle, RefreshCw,
  TrendingUp, TrendingDown, Minus, ExternalLink,
  Zap, Layers, BarChart2, Package, History,
} from 'lucide-react';
import { authFetch } from '@/utils/auth-fetch';
import { useSite } from '@/contexts/SiteContext';
import { catalogColors } from '@/components/catalog/catalogColors';

// ─── Types ────────────────────────────────────────────────────────────────────

interface FeedQuality {
  tier: string | null;
  chs: number | null;
  avg_phs: number | null;
  avg_phs_cl4_high: number | null;
  snapshot_date: string | null;
  total_products: number | null;
  cl4_high_products: number | null;
  tier_distribution: Record<string, number> | null;
  top_missing_attrs: { attr: string; count: number }[] | null;
}

interface KgReadiness {
  total_categories: number;
  ready_count: number;
  avg_score: number | null;
  high_tier_count: number;
  low_tier_count: number;
}

interface CompetitorCoverage {
  total_categories: number;
  healthy_count: number;
  low_coverage_count: number;
  avg_coverage_pct: number | null;
  min_coverage_pct: number | null;
}

interface ComparisonHealth {
  published_apg_count: number;
  a1_stale_apg_count: number;
  a2_attr_gap_apg_count: number;
  feed_gap_apg_count: number;
  fully_populated_apg_count: number;
  comparison_health_pct: number | null;
  snapshot_date: string | null;
}

interface StaleSnapshots {
  count: number;
  apgIds: string[];
}

interface OpenIssues {
  total: number;
  byOwner: Record<string, number>;
  bySeverity: Record<string, number>;
}

interface Delta7d {
  comparisonHealth?: { current: number | null; previous: number | null; delta: number | null };
  staleResolved7d?: number;
  feedQuality?: { currentScore: number | null; previousScore: number | null; delta: number | null };
}

interface Waterfall {
  siteId: string;
  feedQuality: FeedQuality | null;
  kgReadiness: KgReadiness | null;
  competitorCoverage: CompetitorCoverage | null;
  comparisonHealth: ComparisonHealth | null;
  staleSnapshots: StaleSnapshots | null;
  openIssues: OpenIssues;
  delta7d: Delta7d;
}

// GET /api/admin/cs-quality/customer/:siteId/timeline (PRD#9 deliverable 2)
type TimelineEvent =
  | {
      type: 'diagnostic_run';
      date: string;
      startedAt: string;
      completedAt: string | null;
      triggerType: string;
      status: string;
      scope: string | null;
      categoryPath: string | null;
      summary: Record<string, unknown> | null;
    }
  | {
      type: 'quality_change';
      date: string;
      fromTier: string;
      toTier: string;
      fromShs: number;
      toShs: number;
      direction: 'improved' | 'degraded';
    };

// ─── Utilities ────────────────────────────────────────────────────────────────

const pct = (v: number | null | undefined, decimals = 0) =>
  v == null ? '—' : `${(v * 100).toFixed(decimals)}%`;

const feedTierColor: Record<string, string> = {
  AAA: '#15803d', AA: '#1d4ed8', A: '#ca8a04', B: '#ea580c', C: '#dc2626',
};

function DeltaChip({ delta, unit = '%' }: { delta: number | null | undefined; unit?: string }) {
  if (delta == null) return <span className="text-xs text-muted-foreground">no prior data</span>;
  const formatted = unit === '%' ? `${delta >= 0 ? '+' : ''}${(delta * 100).toFixed(1)}%` : `${delta >= 0 ? '+' : ''}${delta}`;
  if (Math.abs(delta) < 0.001) return (
    <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
      <Minus size={12} /> {formatted} vs 7d
    </span>
  );
  const isGood = delta > 0;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium ${isGood ? 'text-green-600' : 'text-red-600'}`}>
      {isGood ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
      {formatted} vs 7d
    </span>
  );
}

function HealthBar({ value, label }: { value: number | null; label?: string }) {
  const pctVal = value != null ? Math.round(value * 100) : null;
  const color = pctVal == null ? '#9CA3AF'
    : pctVal >= 90 ? '#15803d' : pctVal >= 70 ? '#1d4ed8'
    : pctVal >= 50 ? '#ca8a04' : '#dc2626';
  return (
    <div className="flex flex-col gap-1">
      {label && <span className="text-xs text-muted-foreground">{label}</span>}
      <div className="flex items-center gap-2">
        <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
          <div className="h-full rounded-full transition-all" style={{ width: `${pctVal ?? 0}%`, backgroundColor: color }} />
        </div>
        <span className="text-xs font-mono w-10 text-right">{pctVal != null ? `${pctVal}%` : '—'}</span>
      </div>
    </div>
  );
}

function SeverityBadge({ severity }: { severity: string }) {
  const colors: Record<string, string> = {
    critical: 'bg-red-100 text-red-800 border-red-200',
    high:     'bg-orange-100 text-orange-800 border-orange-200',
    medium:   'bg-yellow-100 text-yellow-800 border-yellow-200',
    low:      'bg-blue-100 text-blue-800 border-blue-200',
  };
  return (
    <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-bold uppercase border ${colors[severity] ?? 'bg-gray-100 text-gray-600'}`}>
      {severity}
    </span>
  );
}

// ─── Waterfall sections ───────────────────────────────────────────────────────

function FeedQualitySection({ data, delta }: { data: FeedQuality | null; delta: Delta7d }) {
  if (!data) return (
    <div className="text-sm text-muted-foreground italic">Feed quality data not yet available — run the daily catalog health job.</div>
  );
  const tier = data.tier ?? '—';
  const tierColor = feedTierColor[tier] ?? '#9CA3AF';
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <span className="text-2xl font-bold" style={{ color: tierColor }}>{tier}</span>
        <span className="text-sm text-muted-foreground">
          {data.snapshot_date ? `Snapshot: ${data.snapshot_date}` : 'No snapshot date'}
        </span>
        <DeltaChip delta={delta.feedQuality?.delta} />
      </div>
      <HealthBar value={data.chs ?? data.avg_phs} label="Overall health (PHS)" />
      <HealthBar value={data.avg_phs_cl4_high} label="High-demand category health" />
      {data.total_products != null && (
        <div className="grid grid-cols-2 gap-2 text-xs">
          {([
            ['Total products', data.total_products, catalogColors.gray],
            ['High-tier (cl4)', data.cl4_high_products ?? 0, '#15803d'],
          ] as [string, number, string][]).map(([label, val, color]) => (
            <div key={label} className="text-center p-2 rounded border">
              <div className="font-bold text-base" style={{ color }}>{val.toLocaleString()}</div>
              <div className="text-muted-foreground">{label}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function KgReadinessSection({ data }: { data: KgReadiness | null }) {
  if (!data) return <div className="text-sm text-muted-foreground italic">KG readiness data not yet available.</div>;
  const readyPct = data.total_categories > 0 ? data.ready_count / data.total_categories : null;
  return (
    <div className="space-y-3">
      <HealthBar value={readyPct} label={`${data.ready_count} / ${data.total_categories} categories ready`} />
      <div className="grid grid-cols-3 gap-2 text-xs">
        {([
          ['Avg score', pct(data.avg_score, 1), catalogColors.accentTeal],
          ['High tier (AAA/AA)', data.high_tier_count, '#15803d'],
          ['Low tier (B/C)', data.low_tier_count, '#dc2626'],
        ] as [string, string | number, string][]).map(([label, val, color]) => (
          <div key={label} className="text-center p-2 rounded border">
            <div className="font-bold text-sm" style={{ color }}>{val}</div>
            <div className="text-muted-foreground">{label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function CompetitorCoverageSection({ data }: { data: CompetitorCoverage | null }) {
  if (!data) return <div className="text-sm text-muted-foreground italic">Competitor coverage data not yet available — run the D2 scanner.</div>;
  const healthyPct = data.total_categories > 0 ? data.healthy_count / data.total_categories : null;
  return (
    <div className="space-y-3">
      <HealthBar value={healthyPct} label={`${data.healthy_count} / ${data.total_categories} categories ≥ 70% coverage`} />
      <div className="grid grid-cols-2 gap-2 text-xs">
        <div className="text-center p-2 rounded border">
          <div className="font-bold text-sm" style={{ color: catalogColors.accentTeal }}>{pct(data.avg_coverage_pct, 1)}</div>
          <div className="text-muted-foreground">Avg coverage</div>
        </div>
        <div className="text-center p-2 rounded border">
          <div className="font-bold text-sm" style={{ color: '#dc2626' }}>{pct(data.min_coverage_pct, 1)}</div>
          <div className="text-muted-foreground">Min coverage</div>
        </div>
      </div>
      {(data.low_coverage_count ?? 0) > 0 && (
        <p className="text-xs text-orange-600">{data.low_coverage_count} categories below 70% coverage — escalate to KG team.</p>
      )}
    </div>
  );
}

function ComparisonHealthSection({ data, delta }: { data: ComparisonHealth | null; delta: Delta7d }) {
  if (!data) return <div className="text-sm text-muted-foreground italic">Comparison health data not yet available — run the daily D3 snapshot job.</div>;
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <HealthBar value={data.comparison_health_pct} label="Comparison health" />
        <DeltaChip delta={delta.comparisonHealth?.delta} />
      </div>
      <div className="grid grid-cols-2 gap-2 text-xs sm:grid-cols-4">
        {([
          ['Published APGs', data.published_apg_count, catalogColors.gray],
          ['Fully populated', data.fully_populated_apg_count, '#15803d'],
          ['A1 stale', data.a1_stale_apg_count, '#dc2626'],
          ['A2 attr gap', data.a2_attr_gap_apg_count, '#ea580c'],
        ] as [string, number, string][]).map(([label, val, color]) => (
          <div key={label} className="text-center p-2 rounded border">
            <div className="font-bold text-sm" style={{ color }}>{(val ?? 0).toLocaleString()}</div>
            <div className="text-muted-foreground">{label}</div>
          </div>
        ))}
      </div>
      {data.snapshot_date && <p className="text-xs text-muted-foreground">Snapshot: {data.snapshot_date}</p>}
    </div>
  );
}

function OpenIssuesSection({ data }: { data: OpenIssues }) {
  if (data.total === 0) return (
    <div className="flex items-center gap-2 text-green-700 text-sm">
      <CheckCircle2 size={16} /> No open issues — run a diagnostic to check for new ones.
    </div>
  );
  const ownerLabels: Record<string, string> = {
    cs_quality_bridge:    'CS Quality Bridge',
    kg_team:              'KG Team',
    customer_data_team:   'Customer Data Team',
    cs_vendor_offering:   'CS Vendor Offering',
  };
  return (
    <div className="space-y-2">
      <p className="text-sm font-medium">{data.total} open issue{data.total !== 1 ? 's' : ''}</p>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {Object.entries(data.bySeverity).sort().map(([sev, cnt]) => (
          <div key={sev} className="flex items-center gap-2 p-2 rounded border text-xs">
            <SeverityBadge severity={sev} />
            <span className="font-bold">{cnt}</span>
          </div>
        ))}
      </div>
      <div className="space-y-1">
        {Object.entries(data.byOwner).map(([owner, cnt]) => (
          <div key={owner} className="flex justify-between text-xs text-muted-foreground border-b pb-1 last:border-0">
            <span>{ownerLabels[owner] ?? owner}</span>
            <span className="font-medium text-foreground">{cnt}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Action panel ─────────────────────────────────────────────────────────────

interface ActionState { loading: boolean; result: string | null; error: string | null }
const IDLE: ActionState = { loading: false, result: null, error: null };

function ActionButton({
  label, icon: Icon, onClick, state, disabled,
}: {
  label: string;
  icon: React.ElementType;
  onClick: () => void;
  state: ActionState;
  disabled?: boolean;
}) {
  return (
    <div className="space-y-1">
      <Button
        variant="outline"
        size="sm"
        className="w-full justify-start gap-2"
        onClick={onClick}
        disabled={disabled || state.loading}
      >
        {state.loading ? <RefreshCw size={14} className="animate-spin" /> : <Icon size={14} />}
        {label}
      </Button>
      {state.result && <p className="text-xs text-green-700 pl-1">{state.result}</p>}
      {state.error  && <p className="text-xs text-red-600 pl-1">{state.error}</p>}
    </div>
  );
}

// ─── Customer health timeline (PRD#9 deliverable 2) ─────────────────────────────

const timelineDate = (iso: string) => {
  // quality_change events carry a date-only snapshot_date (YYYY-MM-DD). new Date('2026-07-16')
  // parses as UTC midnight, so toLocaleDateString() shows the PREVIOUS day in timezones west of
  // UTC. Parse date-only strings as a LOCAL date to avoid the off-by-one; full ISO timestamps
  // (diagnostic completed_at/started_at) keep their normal instant parsing.
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  const d = dateOnly
    ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
    : new Date(iso);
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

function TimelineSection({
  events,
  error,
}: {
  events: TimelineEvent[];
  error: string | null;
}) {
  if (error) {
    return (
      <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 flex items-center gap-2">
        <AlertCircle size={13} className="shrink-0" />
        <span>Timeline unavailable ({error}). This is a load error, not "no history".</span>
      </div>
    );
  }
  if (events.length === 0) {
    return (
      <p className="text-sm text-muted-foreground italic">
        No quality events in this window yet. Events appear here as diagnostics run and quality tiers change.
      </p>
    );
  }
  return (
    <ol className="relative space-y-3">
      {events.map((e, i) => {
        if (e.type === 'quality_change') {
          const improved = e.direction === 'improved';
          const Icon = improved ? TrendingUp : TrendingDown;
          const color = improved ? '#15803d' : '#dc2626';
          return (
            <li key={`qc-${i}`} className="flex items-start gap-2.5">
              <Icon size={15} style={{ color }} className="mt-0.5 shrink-0" />
              <div className="text-sm">
                <span className="font-medium" style={{ color }}>
                  Quality {improved ? 'improved' : 'degraded'} {e.fromTier} → {e.toTier}
                </span>
                <span className="text-muted-foreground">
                  {' '}({Math.round(e.fromShs * 100)}% → {Math.round(e.toShs * 100)}%)
                </span>
                <div className="text-xs text-muted-foreground">{timelineDate(e.date)}</div>
              </div>
            </li>
          );
        }
        // diagnostic_run
        const issues = e.summary && typeof e.summary.upserted === 'number' ? (e.summary.upserted as number) : null;
        return (
          <li key={`dr-${i}`} className="flex items-start gap-2.5">
            <Zap size={15} className="mt-0.5 shrink-0 text-sky-600" />
            <div className="text-sm">
              <span className="font-medium">Diagnostic ran</span>
              <span className="text-muted-foreground">
                {' '}({e.triggerType}
                {e.scope && e.scope !== 'site' ? `, ${e.scope}` : ''}
                {e.status && e.status !== 'completed' ? ` — ${e.status}` : ''})
                {issues != null ? ` · ${issues} issue${issues === 1 ? '' : 's'} tracked` : ''}
              </span>
              <div className="text-xs text-muted-foreground">{timelineDate(e.date)}</div>
            </div>
          </li>
        );
      })}
    </ol>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function CsQualityBridge() {
  const { selectedSite } = useSite();
  const siteId = selectedSite?.id ?? null;

  const [loading, setLoading]     = useState(false);
  const [waterfall, setWaterfall] = useState<Waterfall | null>(null);
  const [error, setError]         = useState<string | null>(null);
  // Timeline is bound to the siteId it was loaded for, so a stale site-A timeline can never render
  // under site-B during the render-before-reset-effect window (codex #4070 BLOCK — same pattern as
  // visibleWaterfall). Consumed via `visibleTimeline` below, guarded on siteId.
  const [timelineState, setTimelineState] = useState<{ siteId: string; events: TimelineEvent[]; error: string | null } | null>(null);
  // Independent loading flag for the decoupled timeline, so the card shows a skeleton (not the
  // "No quality events" empty state) while /timeline is still pending after the waterfall resolves.
  const [timelineLoading, setTimelineLoading] = useState(false);

  const [diagState,   setDiagState]   = useState<ActionState>(IDLE);
  const [regenState,  setRegenState]  = useState<ActionState>(IDLE);
  const [kgState,     setKgState]     = useState<ActionState>(IDLE);
  const [feedState,   setFeedState]   = useState<ActionState>(IDLE);
  const [vendorState, setVendorState] = useState<ActionState>(IDLE);

  // Cancel token: each loadWaterfall call creates a fresh token object and
  // marks the previous one cancelled, so a stale in-flight response for an
  // old site cannot overwrite state after a site switch.
  const loadTokenRef = useRef<{ cancelled: boolean }>({ cancelled: false });
  // Separate cancel token for the independent timeline request (decoupled from the waterfall).
  const timelineTokenRef = useRef<{ cancelled: boolean }>({ cancelled: false });

  // Assigned during render (not in useEffect) so it is immediately consistent
  // with the current siteId — no render-to-effect timing window.
  const currentSiteIdRef = useRef<string | null>(siteId);
  currentSiteIdRef.current = siteId;

  const loadWaterfall = useCallback(async () => {
    loadTokenRef.current.cancelled = true;
    const token = { cancelled: false };
    loadTokenRef.current = token;

    if (!siteId) {
      setWaterfall(null);
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await authFetch(`/api/admin/cs-quality/customer/${siteId}`);
      const data = await res.json();
      if (token.cancelled) return;
      if (!res.ok || !data.success) throw new Error(data.error ?? 'Failed to load waterfall');
      setWaterfall(data as Waterfall);
    } catch (e: unknown) {
      if (token.cancelled) return;
      setWaterfall(null);
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      if (!token.cancelled) setLoading(false);
    }
  }, [siteId]);

  // The timeline is a SEPARATE, independent request from the waterfall (codex #4066 lesson): it
  // must never block the waterfall render, so it runs on its own effect + cancel token. A failure
  // surfaces as a distinct "Timeline unavailable" warning, never a silently-empty list.
  const loadTimeline = useCallback(async () => {
    timelineTokenRef.current.cancelled = true;
    const token = { cancelled: false };
    timelineTokenRef.current = token;

    if (!siteId) {
      setTimelineState(null);
      setTimelineLoading(false);
      return;
    }
    const forSiteId = siteId; // bind the result to the site it was requested for
    setTimelineLoading(true);
    try {
      const res = await authFetch(`/api/admin/cs-quality/customer/${forSiteId}/timeline`);
      const data = await res.json().catch(() => ({} as Record<string, unknown>));
      if (token.cancelled) return;
      if (!res.ok || !Array.isArray((data as { events?: unknown }).events)) {
        const td = data as { error?: string; message?: string };
        throw new Error(td.error ?? td.message ?? `HTTP ${res.status}`);
      }
      setTimelineState({ siteId: forSiteId, events: (data as { events: TimelineEvent[] }).events, error: null });
    } catch (e: unknown) {
      if (token.cancelled) return;
      setTimelineState({ siteId: forSiteId, events: [], error: e instanceof Error ? e.message : String(e) });
    } finally {
      if (!token.cancelled) setTimelineLoading(false);
    }
  }, [siteId]);

  useEffect(() => { loadWaterfall(); }, [loadWaterfall]);
  useEffect(() => { loadTimeline(); }, [loadTimeline]);

  // Reset all action button states on site switch so buttons are never left
  // in a permanently-loading/disabled state from a prior site's in-flight request.
  // The stale-site guard in runAction/handleRegenerate still suppresses any
  // result or error text from the old site resolving after the switch.
  useEffect(() => {
    setWaterfall(null);
    setTimelineState(null);
    // NOTE: timelineLoading is owned entirely by loadTimeline (set true on load, false in finally
    // / no-site). Resetting it here would clobber the `true` that loadTimeline's effect just set
    // (this effect runs after it), making the card flash its empty state during load.
    setDiagState(IDLE);
    setRegenState(IDLE);
    setKgState(IDLE);
    setFeedState(IDLE);
    setVendorState(IDLE);
  }, [siteId]);

  const runAction = useCallback(async (
    endpoint: string,
    method: 'POST' | 'GET',
    setState: (s: ActionState) => void,
    successMsg: (data: Record<string, unknown>) => string,
  ) => {
    if (!siteId) return;
    const actionSiteId = siteId;
    setState({ loading: true, result: null, error: null });
    try {
      const res = await authFetch(`/api/admin/cs-quality/customer/${actionSiteId}/${endpoint}`, { method });
      const data = await res.json();
      // Guard: if the user switched sites while the request was in flight, discard
      // the result entirely — showing site-A data on site-B's page is misleading.
      if (currentSiteIdRef.current !== actionSiteId) return;
      if (!res.ok) {
        setState({ loading: false, result: null, error: data.message ?? data.error ?? `HTTP ${res.status}` });
        return;
      }
      setState({ loading: false, result: successMsg(data), error: null });
      // A POST (e.g. run-diagnostic) changes state that both surfaces feed — refresh both,
      // independently, so a new diagnostic run also appears in the timeline.
      if (method === 'POST') { loadWaterfall(); loadTimeline(); }
    } catch (e: unknown) {
      if (currentSiteIdRef.current !== actionSiteId) return;
      setState({ loading: false, result: null, error: e instanceof Error ? e.message : String(e) });
    }
  }, [siteId, loadWaterfall, loadTimeline]);

  const handleRunDiagnostic = () => runAction(
    'run-diagnostic', 'POST', setDiagState,
    d => {
      const upserted = (d.upserted as number) ?? 0;
      const resolved = (d.resolved as number) ?? 0;
      const errors  = d.errors   as string[] | undefined;
      const warns   = d.warnings as string[] | undefined;
      if (d.partial) {
        const parts = [`Partial diagnostic: ${upserted} upserted, ${resolved} resolved`];
        if (errors?.length)  parts.push(`Scanner errors: ${errors.join('; ')}`);
        if (warns?.length)   parts.push(`Warnings: ${warns.join('; ')}`);
        return parts.join('. ');
      }
      const base = `Diagnostic complete: ${upserted} issues upserted, ${resolved} resolved`;
      return warns?.length ? `${base}. Warning: ${warns.join('; ')}` : base;
    },
  );

  const handleRegenerate = useCallback(async () => {
    if (!siteId) return;
    const actionSiteId = siteId;
    setRegenState({ loading: true, result: null, error: null });
    try {
      const res = await authFetch(`/api/admin/cs-quality/stale-snapshots/${actionSiteId}/regenerate-all`, { method: 'POST' });
      const data = await res.json();
      if (currentSiteIdRef.current !== actionSiteId) return;
      if (!res.ok || !data.success) {
        setRegenState({ loading: false, result: null, error: data.error ?? `HTTP ${res.status}` });
        return;
      }
      setRegenState({ loading: false, result: `Queued ${data.queued} APG(s) for regeneration`, error: null });
      loadWaterfall();
    } catch (e: unknown) {
      if (currentSiteIdRef.current !== actionSiteId) return;
      setRegenState({ loading: false, result: null, error: e instanceof Error ? e.message : String(e) });
    }
  }, [siteId, loadWaterfall]);

  const handleKgEscalate = () => runAction(
    'kg-escalate', 'POST', setKgState,
    d => `Escalated ${(d.categoriesEscalated as number) ?? 0} categories (task: ${(d.taskId as string) ?? '?'})`,
  );

  const handleFeedReport = () => runAction(
    'feed-report', 'POST', setFeedState,
    () => 'Feed report started',
  );

  const handleVendorOffer = () => runAction(
    'vendor-offer', 'POST', setVendorState,
    () => 'Opportunity scores refreshed',
  );

  const noSite = !siteId;
  // Guard against the useEffect timing window: waterfall state holds site-A data
  // until the useEffect reset fires after the first render with the new siteId.
  const visibleWaterfall = waterfall?.siteId === siteId ? waterfall : null;
  // Same siteId guard for the timeline: only render events/error that belong to the CURRENT site,
  // so a stale site-A timeline never flashes under site-B before the reset effect runs.
  const visibleTimeline = timelineState?.siteId === siteId ? timelineState : null;

  const sections = [
    {
      title: 'Feed Quality',
      icon: BarChart2,
      color: catalogColors.accentTeal,
      content: <FeedQualitySection data={visibleWaterfall?.feedQuality ?? null} delta={visibleWaterfall?.delta7d ?? {}} />,
    },
    {
      title: 'KG Readiness',
      icon: Layers,
      color: catalogColors.infoBlue,
      content: <KgReadinessSection data={visibleWaterfall?.kgReadiness ?? null} />,
    },
    {
      title: 'Competitor Coverage (D2)',
      icon: BarChart2,
      color: catalogColors.amber,
      content: <CompetitorCoverageSection data={visibleWaterfall?.competitorCoverage ?? null} />,
    },
    {
      title: 'Comparison Table Health (D3)',
      icon: CheckCircle2,
      color: catalogColors.purple,
      content: <ComparisonHealthSection data={visibleWaterfall?.comparisonHealth ?? null} delta={visibleWaterfall?.delta7d ?? {}} />,
    },
    {
      title: `Stale Snapshots (D1)${visibleWaterfall?.staleSnapshots ? ` — ${visibleWaterfall.staleSnapshots.count} unresolved` : ''}`,
      icon: AlertTriangle,
      color: visibleWaterfall?.staleSnapshots && visibleWaterfall.staleSnapshots.count > 0 ? '#dc2626' : catalogColors.accentTeal,
      content: visibleWaterfall ? (
        <div className="space-y-2">
          {visibleWaterfall.staleSnapshots == null ? (
            <p className="text-sm text-muted-foreground">D1 scanner table not yet available — run pending migrations.</p>
          ) : visibleWaterfall.staleSnapshots.count > 0 ? (
            <>
              <p className="text-sm text-orange-700">{visibleWaterfall.staleSnapshots.count} APG(s) with stale competitor snapshots.</p>
              {visibleWaterfall.delta7d.staleResolved7d != null && (
                <p className="text-xs text-muted-foreground">{visibleWaterfall.delta7d.staleResolved7d} resolved in past 7 days.</p>
              )}
            </>
          ) : (
            <div className="flex items-center gap-2 text-green-700 text-sm">
              <CheckCircle2 size={16} /> No unresolved stale snapshots.
            </div>
          )}
        </div>
      ) : null,
    },
    {
      title: 'Open Issues',
      icon: AlertCircle,
      color: (visibleWaterfall?.openIssues.total ?? 0) > 0 ? '#dc2626' : catalogColors.accentTeal,
      content: <OpenIssuesSection data={visibleWaterfall?.openIssues ?? { total: 0, byOwner: {}, bySeverity: {} }} />,
    },
  ];

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold">CS Quality Bridge</h1>
          <p className="text-sm text-muted-foreground">
            Per-customer catalog quality waterfall — Feed Quality → KG Readiness → Coverage → Comparison Health → Issues
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => { loadWaterfall(); loadTimeline(); }} disabled={loading || noSite} className="gap-2">
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          Refresh
        </Button>
      </div>

      {noSite && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>Select a site from the header to load the quality waterfall.</AlertDescription>
        </Alert>
      )}

      {error && (
        <Alert variant="destructive">
          <XCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Waterfall */}
        <div className="lg:col-span-2 space-y-4">
          {sections.map(({ title, icon: Icon, color, content }) => (
            <Card key={title}>
              <CardHeader className="pb-2 pt-4 px-4">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <Icon size={14} style={{ color }} />
                  {title}
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4">
                {loading ? (
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-3/4" />
                  </div>
                ) : content}
              </CardContent>
            </Card>
          ))}

          {/* Customer health timeline (PRD#9 deliverable 2) — "what changed this month?" */}
          {!noSite && (
            <Card>
              <CardHeader className="pb-2 pt-4 px-4">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <History size={14} style={{ color: catalogColors.infoBlue }} />
                  Customer Health Timeline
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4">
                {timelineLoading ? (
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-2/3" />
                  </div>
                ) : (
                  <TimelineSection events={visibleTimeline?.events ?? []} error={visibleTimeline?.error ?? null} />
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Actions panel */}
        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-2 pt-4 px-4">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Zap size={14} style={{ color: catalogColors.amber }} />
                Actions
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4 space-y-3">
              <ActionButton
                label="Run diagnostic"
                icon={RefreshCw}
                onClick={handleRunDiagnostic}
                state={diagState}
                disabled={noSite}
              />
              <div className="border-t pt-3 space-y-1">
                <p className="text-[10px] text-muted-foreground uppercase font-semibold tracking-wide mb-2">Remediation</p>
                <ActionButton
                  label="Regenerate stale pages"
                  icon={Zap}
                  onClick={handleRegenerate}
                  state={regenState}
                  disabled={noSite || (visibleWaterfall?.staleSnapshots?.count ?? 0) === 0}
                />
                <ActionButton
                  label="Escalate KG gap"
                  icon={Layers}
                  onClick={handleKgEscalate}
                  state={kgState}
                  disabled={noSite}
                />
                <ActionButton
                  label="Send feed report (Phase 5)"
                  icon={ExternalLink}
                  onClick={handleFeedReport}
                  state={feedState}
                  disabled={true}
                />
                <ActionButton
                  label="Score vendor opportunities (Phase 6)"
                  icon={Package}
                  onClick={handleVendorOffer}
                  state={vendorState}
                  disabled={noSite}
                />
              </div>
            </CardContent>
          </Card>

          {/* 7-day delta card */}
          {visibleWaterfall && (
            <Card>
              <CardHeader className="pb-2 pt-4 px-4">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <TrendingUp size={14} style={{ color: catalogColors.accentTeal }} />
                  7-Day Delta
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4 space-y-3 text-xs">
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Comparison health</span>
                  <DeltaChip delta={visibleWaterfall.delta7d.comparisonHealth?.delta} />
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Feed quality score</span>
                  <DeltaChip delta={visibleWaterfall.delta7d.feedQuality?.delta} />
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Stale APGs resolved</span>
                  <span className="font-medium text-green-700">{visibleWaterfall.delta7d.staleResolved7d ?? 0}</span>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
